import { useEffect, type ReactNode } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'

import { DisabledWorkspace } from './disabled-workspace'
import { useAuth } from '@/lib/auth/auth-context'
import { clearAuthSessionTransition, isAuthSessionTransitionActive } from '@/lib/auth/session-transition'

/**
 * Full-screen loader shown while authentication is being confirmed. Mirrors the
 * SPA-shell spinner (brand-colored ring on the app canvas) so the transition
 * from the prerendered shell into the app is seamless — no blank/white frame.
 */
export function AuthLoadingScreen({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas px-6 py-12">
      <div className="flex flex-col items-center gap-4">
        <div
          className="h-9 w-9 animate-spin rounded-full border-2 border-line border-t-brand"
          aria-hidden="true"
        />
        <p className="text-sm font-medium text-ink-soft" role="status">
          {label}
        </p>
      </div>
    </div>
  )
}

/**
 * Gate for protected pages. Renders the loader until auth is confirmed, then:
 *  - redirects unauthenticated viewers to /login,
 *  - redirects viewers without a workspace to /onboarding (mode 'workspace'),
 *  - otherwise renders the protected content.
 *
 * mode 'session' only requires a signed-in session (used by /onboarding itself,
 * which must render for the needs-workspace state).
 *
 * It never returns bare null: while a redirect is in flight it keeps the loader
 * on screen, so logout / session-expiry / first-load never flash a white page.
 */
export function RequireAuth({
  children,
  mode = 'workspace',
}: {
  children: ReactNode
  mode?: 'workspace' | 'session'
}) {
  const { status } = useAuth()
  const navigate = useNavigate()

  // While an intentional auth navigation is in flight (e.g. signing out right
  // after a password change), let the originating page drive routing instead of
  // racing it with a generic redirect — otherwise we'd send the viewer to a bare
  // /login and drop the page's own /login?account=password-updated target. The
  // flag is short-lived (15s) and cleared by /login on mount and on admit below.
  const inAuthTransition = isAuthSessionTransitionActive()

  // mode 'session' (onboarding) admits a signed-in viewer who has no workspace
  // yet; mode 'workspace' requires a fully provisioned, active workspace.
  const isAdmitted = status === 'authenticated' || (status === 'needs-workspace' && mode === 'session')

  let redirectTo: string | null = null
  if (!inAuthTransition && !isAdmitted) {
    if (status === 'unauthenticated') {
      redirectTo = '/login'
    } else if (status === 'needs-workspace') {
      redirectTo = '/onboarding'
    }
  }

  useEffect(() => {
    if (redirectTo) {
      navigate(redirectTo, { replace: true })
    }
  }, [redirectTo, navigate])

  // Reaching the app with a confirmed session means any pending transition has
  // completed — clear the flag so a later visit to /login redirects normally.
  useEffect(() => {
    if (status === 'authenticated') {
      clearAuthSessionTransition()
    }
  }, [status])

  if (status === 'disabled') return <DisabledWorkspace />

  if (isAdmitted) {
    return <>{children}</>
  }

  // loading, redirecting, or waiting out a transition: always a clean loader.
  return <AuthLoadingScreen />
}

/**
 * Gate for the login / signup / forgot-password entry points. Keeps signed-in
 * viewers off these pages: they see the loader and are routed to the app
 * (/dashboard) or onboarding instead of the login form. Used as a layout route,
 * so it renders the nested auth page via <Outlet /> only once the viewer is
 * confirmed unauthenticated.
 */
export function GuestOnlyRoute() {
  const { status } = useAuth()
  const navigate = useNavigate()

  // A just-submitted signup sets the transition flag (and stores its session)
  // before its workspace exists. Without this guard, the new needs-workspace
  // state would bounce the still-mounted SignUpPage to /onboarding mid-flow,
  // killing its own initializeWorkspace + navigate('/dashboard'). While the
  // transition is active, keep the page mounted and let it route itself.
  const inAuthTransition = isAuthSessionTransitionActive()

  let redirectTo: string | null = null
  if (!inAuthTransition) {
    if (status === 'authenticated' || status === 'disabled') {
      redirectTo = '/dashboard'
    } else if (status === 'needs-workspace') {
      redirectTo = '/onboarding'
    }
  }

  useEffect(() => {
    if (redirectTo) {
      navigate(redirectTo, { replace: true })
    }
  }, [redirectTo, navigate])

  if (inAuthTransition) {
    return <Outlet />
  }

  if (status === 'loading' || redirectTo) {
    return <AuthLoadingScreen />
  }

  return <Outlet />
}
